import { mapOptionsToSearchListingsParams } from '@pkgs/api/utils/mapParams';
import { findLocalityPlaceId } from '@pkgs/api/utils/location';
import { getConfigWithMetadata } from '@pkgs/api/utils/headers';
import { getSimilarListingsPriceInfo, mapSearchListingsFiltersToSearchFilterRequest } from '@pkgs/api/utils/filters';
import { getErrorResult } from '@pkgs/api/utils/error';
import { type ApiConfig, type ApiResult, type SearchListingsFilters, type SearchListingsOptions } from '@pkgs/api/types';
import {
  Currency,
  type Listing,
  ListingSortOption,
  PropertyKind,
  PropertyMarketingType,
  searchListings,
  type SearchListingsResponse,
} from '@generated/search-bff';

/**
 * Fetch property listings that are similar to a given listing using the `/api/v2/listing/search` endpoint.
 *
 * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/listings/getSimilarListings Search API Documentation}
 */
export async function getSimilarListings(
  property: Listing,
  options: SearchListingsOptions,
  apiConfig: ApiConfig,
): Promise<ApiResult<SearchListingsResponse>> {
  try {
    const _apiConfig = getConfigWithMetadata(apiConfig, 'getSimilarListings');
    const localityPlaceId = property.placeIds ? findLocalityPlaceId(property.placeIds) : undefined;

    if (!localityPlaceId) {
      throw new Error('The property does not have any locality place id');
    }

    const { businessArea, propertyMarketingType, isDevelopmentProject, hasPriceOnRequest, id } = property;

    const filters: SearchListingsFilters = {
      excludedPropertyIds: [id],
      businessArea: [businessArea],
      propertyMarketingType: [propertyMarketingType],
      placeIds: localityPlaceId ? [localityPlaceId] : [],
      sortingOptions: [
        propertyMarketingType === PropertyMarketingType.rent ? ListingSortOption.RENT_DESC : ListingSortOption.SALES_PRICE_DESC,
      ],
    };

    const similarPropertiesPriceInfo = getSimilarListingsPriceInfo(property);

    if (isDevelopmentProject) {
      filters.propertyTypeSubType = { group: [] };
      filters.propertyKind = PropertyKind.group;
    } else {
      filters.propertyKind = PropertyKind.object;
      filters.searchRadius = 5;

      if (hasPriceOnRequest) {
        filters.priceOnRequest = true;
      } else if (similarPropertiesPriceInfo) {
        filters.price = similarPropertiesPriceInfo.rangeFilter;
      } else {
        return {
          status: 'success',
          data: {
            listings: [],
            listingsTotal: 0,
            widerAreaListings: [],
          },
        };
      }
    }

    const params = mapOptionsToSearchListingsParams(options);

    const data = await searchListings(
      mapSearchListingsFiltersToSearchFilterRequest(
        filters,
        { ...options, currency: property.baseCurrency ?? Currency.EUR },
        similarPropertiesPriceInfo?.priceType,
      ),
      params,
      _apiConfig,
    );

    return {
      status: 'success',
      data,
    };
  } catch (error) {
    apiConfig.onError?.(error);
    return getErrorResult(error);
  }
}
